import React, { useState, useRef, useEffect } from 'react';
import { TeamMember } from '../types';
import { ChevronDown, User, X } from '../../../icons';

interface AssignmentDropdownProps {
  currentAssignee?: string; // userId
  teamMembers: TeamMember[];
  onAssign: (userId: string | null) => void;
  disabled?: boolean;
}

/**
 * AssignmentDropdown
 * Dropdown for assigning tasks to team members
 */
const AssignmentDropdown: React.FC<AssignmentDropdownProps> = ({
  currentAssignee,
  teamMembers,
  onAssign,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const currentMember = currentAssignee 
    ? teamMembers.find(m => m.userId === currentAssignee)
    : null;

  const handleSelect = (userId: string | null) => {
    onAssign(userId);
    setIsOpen(false);
  };

  if (disabled) {
    return (
      <div className="px-3 py-1.5 text-sm text-gray-400 bg-gray-100 rounded-lg">
        {currentMember ? (
          <div className="flex items-center space-x-2">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
              style={{ backgroundColor: currentMember.color }}
            >
              {currentMember.userName.charAt(0).toUpperCase()}
            </div>
            <span>{currentMember.userName}</span>
          </div>
        ) : (
          <span>Niet toegewezen</span>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center space-x-2 px-3 py-1.5 text-sm
          border border-gray-300 rounded-lg
          hover:border-orange-500 hover:bg-orange-50
          transition-colors
          ${currentMember ? 'bg-white' : 'bg-gray-50'}
        `}
      >
        {currentMember ? (
          <>
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
              style={{ backgroundColor: currentMember.color }}
            >
              {currentMember.userName.charAt(0).toUpperCase()}
            </div>
            <span className="text-gray-700">{currentMember.userName}</span>
          </>
        ) : (
          <>
            <User size={16} className="text-gray-400" />
            <span className="text-gray-500">Toewijzen</span>
          </>
        )}
        <ChevronDown 
          size={14} 
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="py-1">
            {/* Unassign option */}
            <button
              onClick={() => handleSelect(null)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <X size={16} className="text-gray-400" />
              <span>Niet toewijzen</span>
            </button>
            
            <div className="border-t border-gray-200 my-1" />
            
            {/* Team members */}
            {teamMembers.map((member) => (
              <button
                key={member.userId}
                onClick={() => handleSelect(member.userId)}
                className={`
                  w-full px-4 py-2 text-left text-sm hover:bg-gray-50
                  flex items-center space-x-3
                  ${currentAssignee === member.userId ? 'bg-orange-50' : ''}
                `}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                  style={{ backgroundColor: member.color }}
                >
                  {member.userName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {member.userName}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {member.userEmail}
                  </div>
                </div>
                {currentAssignee === member.userId && (
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentDropdown;
